import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { debugLog, debugError } from '../../../../utils/debugLog';

/**
 * Custom hook for managing AI provider and model state
 *
 * Handles:
 * - Provider fetching from AuthContext
 * - Provider/model synchronization with template data
 * - Model helper functions
 *
 * @returns {Object} Provider state and functions
 */
export const useTemplateProviders = () => {
  const { tenant, refreshAuthContext } = useAuth();
  const [providers, setProviders] = useState([]);
  const [providerModels, setProviderModels] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(false);
  const hasSyncedProvider = useRef(false);
  const hasAttemptedRefresh = useRef(false);

  // Fetch available providers from AuthContext (only configured providers)
  // Users must add providers from Settings first, which updates AWS Secrets Manager
  const fetchProviders = useCallback(async () => {
    try {
      setLoadingProviders(true);

      // Get adapters from AuthContext tenant object (loaded on login from AWS)
      // This is the ONLY source - only show what's actually configured
      debugLog('🔍 useTemplateProviders - Checking tenant.adapters:', {
        hasTenant: !!tenant,
        hasAdapters: !!tenant?.adapters,
        adaptersType: typeof tenant?.adapters,
        adaptersKeys: tenant?.adapters ? Object.keys(tenant.adapters) : [],
        adaptersCount: tenant?.adapters
          ? Object.keys(tenant.adapters).length
          : 0,
        adaptersSample: tenant?.adapters
          ? Object.entries(tenant.adapters)
              .slice(0, 2)
              .map(([k, v]) => ({
                key: k,
                provider: v?.provider,
                model_name: v?.model_name,
              }))
          : null,
      });

      // If adapters are empty, try refreshing once from AWS
      if (
        tenant?.adapters &&
        Object.keys(tenant.adapters).length === 0 &&
        !hasAttemptedRefresh.current
      ) {
        debugLog(
          '🔄 Adapters are empty, attempting to refresh from AWS Secrets Manager...'
        );
        hasAttemptedRefresh.current = true;
        const refreshedTenant = await refreshAuthContext();
        if (
          refreshedTenant?.adapters &&
          Object.keys(refreshedTenant.adapters).length > 0
        ) {
          debugLog('✅ Refresh successful, adapters loaded:', {
            adapterCount: Object.keys(refreshedTenant.adapters).length,
            adapterKeys: Object.keys(refreshedTenant.adapters),
          });
          // Use refreshed tenant for processing
          const refreshedAdapters = refreshedTenant.adapters;
          const adaptersList = [];
          const modelsMap = {};

          for (const [adapterKey, adapterData] of Object.entries(
            refreshedAdapters
          )) {
            const provider = adapterData?.provider || 'unknown';
            const modelName = adapterData?.model_name || adapterKey;

            debugLog('Processing adapter:', {
              adapterKey,
              provider,
              modelName,
              adapterDataKeys: Object.keys(adapterData || {}),
            });

            // Group by provider
            if (!modelsMap[provider]) {
              modelsMap[provider] = [];
              adaptersList.push({
                id: provider,
                name: provider,
                models: [],
              });
            }

            // Add model to provider
            modelsMap[provider].push({
              id: modelName,
              name: modelName,
              value: modelName,
              label: modelName,
            });
          }

          // Update adapters list with models
          adaptersList.forEach(adapter => {
            adapter.models = modelsMap[adapter.id] || [];
          });

          debugLog('✅ Loaded adapters from refreshed AuthContext:', {
            providersCount: adaptersList.length,
            providers: adaptersList.map(p => ({
              id: p.id,
              name: p.name,
              modelsCount: p.models.length,
            })),
            modelsMapKeys: Object.keys(modelsMap),
          });

          setProviders(adaptersList);
          setProviderModels(modelsMap);
          return;
        } else {
          debugLog('⚠️ Refresh did not load adapters, using empty state');
        }
      }

      if (tenant?.adapters) {
        const adaptersFromContext = tenant.adapters;

        // Convert adapters to the format expected by TemplateEditor
        const adaptersList = [];
        const modelsMap = {};

        for (const [adapterKey, adapterData] of Object.entries(
          adaptersFromContext
        )) {
          const provider = adapterData?.provider || 'unknown';
          const modelName = adapterData?.model_name || adapterKey;

          debugLog('Processing adapter:', {
            adapterKey,
            provider,
            modelName,
            adapterDataKeys: Object.keys(adapterData || {}),
          });

          // Group by provider
          if (!modelsMap[provider]) {
            modelsMap[provider] = [];
            adaptersList.push({
              id: provider,
              name: provider,
              models: [],
            });
          }

          // Add model to provider
          modelsMap[provider].push({
            id: modelName,
            name: modelName,
            value: modelName,
            label: modelName,
          });
        }

        // Update adapters list with models
        adaptersList.forEach(adapter => {
          adapter.models = modelsMap[adapter.id] || [];
        });

        debugLog('✅ Loaded adapters from AuthContext:', {
          providersCount: adaptersList.length,
          providers: adaptersList.map(p => ({
            id: p.id,
            name: p.name,
            modelsCount: p.models.length,
          })),
          modelsMapKeys: Object.keys(modelsMap),
        });

        setProviders(adaptersList);
        setProviderModels(modelsMap);
      } else {
        // No adapters in context - show empty state
        // User needs to add providers from Settings first
        setProviders([]);
        setProviderModels({});
        debugLog(
          'ℹ️ No adapters found in AuthContext - user needs to add providers from Settings first'
        );
      }
    } catch (err) {
      debugError('Error loading providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  }, [tenant]);

  // Load providers on mount and when tenant changes
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Helper functions
  const getModelsForProvider = useCallback(
    providerId => {
      // Return models from AWS only - no hardcoded fallbacks
      return providerModels[providerId] || [];
    },
    [providerModels]
  );

  const getDefaultModelForProvider = useCallback(
    providerId => {
      const models = getModelsForProvider(providerId);
      return models[0]?.value || models[0]?.id || '';
    },
    [getModelsForProvider]
  );

  return {
    providers,
    providerModels,
    loadingProviders,
    hasSyncedProvider,
    fetchProviders,
    getModelsForProvider,
    getDefaultModelForProvider,
  };
};
